// GamePage.jsx
// Bloom & Blight — a Leaflings garden duel, playable at /game.
// Full match flow: draft your species → the biome is rolled → race Sprout to
// grow four plants while the forecast die batters both gardens. All rules
// live in gameEngine.js; this file is UI + turn pacing only.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BIOMES,
  BIOME_IDS,
  CARDS,
  COPIES_FOR_SPECIES_COUNT,
  PETALS_TO_WIN,
  SPECIES,
  SPECIES_IDS,
  WEATHER,
} from "./gameData";
import {
  compostCard,
  createDemoGame,
  endTurn,
  getTargets,
  newGame,
  plantCard,
  playBlight,
  playTreatment,
  digUpPlant,
  rollBiome,
  waterPlant,
} from "./gameEngine";
import { chooseAiAction } from "./aiOpponent";
import { PetalFlower } from "./PlantArt";
import LeaflingSprite, { EffectVignette } from "./LeaflingSprite";
import { openInNewTab } from "../sharedFunctions";
import { APP_STORE_URL } from "../constants";
import "./GamePage.css";

const HUMAN = 0;
const AI = 1;
const AI_STEP_MS = 850;
const AI_STEP_CAP = 14; // safety net so a heuristic bug can never hang a turn

function Petals({ count, label }) {
  return (
    <div className="bb-petals" aria-label={`${label}: ${count} of ${PETALS_TO_WIN} petals`}>
      {Array.from({ length: PETALS_TO_WIN }, (_, i) => (
        <span key={i} className={i < count ? "filled" : ""}>❀</span>
      ))}
    </div>
  );
}

function Meter({ icon, value, min, max, needed }) {
  const good = needed === "min" ? value >= min : value >= min && value <= max;
  const target = needed === "min" ? `${min}+` : min === max ? `${min}` : `${min}–${max}`;
  return (
    <div className={`bb-meter ${good ? "good" : ""}`}>
      <span>{icon}</span><strong>{value}</strong><small>needs {target}</small>
    </div>
  );
}

function Plant({ plant, owner, enemy, isTarget, onClick, badge, greenhouseOp, onGreenhouseChange, greenhouseFull }) {
  if (!plant) {
    return (
      <button className={`bb-pot empty ${isTarget ? "target" : ""}`} onClick={onClick} disabled={!isTarget}>
        <span>＋</span><small>{isTarget ? "Plant here" : "Empty pot"}</small>
      </button>
    );
  }
  const species = SPECIES[plant.speciesId];
  return (
    <div className={`bb-plant-wrap ${onGreenhouseChange ? "greenhouse-active" : ""}`}>
      {onGreenhouseChange && (
        <div className="bb-greenhouse-controls" aria-label={`Adjust ${species.name}`}>
          {[["light", 1, "+☀️"], ["water", -1, "−💧"], ["water", 1, "+💧"]].map(([stat, delta, label]) => (
            <button
              key={label}
              className={greenhouseOp?.stat === stat && greenhouseOp?.delta === delta ? "active" : ""}
              disabled={greenhouseFull && !greenhouseOp}
              onClick={() => onGreenhouseChange(stat, delta)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      <button
        className={`bb-plant ${isTarget ? "target" : ""} ${enemy ? "enemy" : ""}`}
        onClick={onClick}
        disabled={!isTarget}
        aria-label={`${owner}'s ${species.name}, ${plant.light} light, ${plant.water} water${isTarget ? ", valid target" : ""}`}
      >
        <div className="bb-plant-top">
          <div className="bb-plant-art">
            <LeaflingSprite speciesId={plant.speciesId} owner={enemy ? "sprout" : "you"} plant={plant} />
          </div>
          <div><strong>{species.name}</strong><small>{species.blurb}</small></div>
        </div>
        <div className="bb-meters">
          <Meter icon="☀️" value={plant.light} min={species.lightNeed} needed="min" />
          <Meter icon="💧" value={plant.water} min={species.waterMin} max={species.waterMax} />
        </div>
        <div className="bb-statuses">
          {plant.growLamp && <span title="Grow Lamp: +1 light after every forecast">🔦 Lamp</span>}
          {plant.humidifier && <span title="Humidifier: weather cannot alter water">🫧 Humidifier</span>}
          {plant.umbrella && <span className="blight" title="Umbrella blocks base weather">☂️ Blighted</span>}
          {badge && <span className="hint">{badge}</span>}
        </div>
      </button>
    </div>
  );
}

function HandCard({ card, selected, disabled, unplayable, onClick }) {
  const def = CARDS[card.id];
  return (
    <button
      className={`bb-card ${def.type} ${selected ? "selected" : ""} ${unplayable ? "unplayable" : ""}`}
      disabled={disabled}
      title={def.text}
      aria-label={`${def.name}, ${def.type}: ${def.text}`}
      onClick={onClick}
    >
      <span className="bb-card-icon">
        {def.type === "plant" ? <LeaflingSprite speciesId={card.id} /> : def.icon}
      </span>
      <span><strong>{def.name}</strong><small>{def.type}</small></span>
    </button>
  );
}

function CardPreview({ card, onClose, onPlay }) {
  const def = CARDS[card.id];
  const species = def.type === "plant" ? SPECIES[card.id] : null;
  const targetCopy = def.type === "plant"
    ? "An empty friendly pot, or a matching plant to compost"
    : def.type === "blight"
      ? "A valid rival Leafling"
      : ["shopping", "recycle", "planter"].includes(card.id)
        ? "No board target"
        : "One or more friendly Leaflings";
  if (species) return (
    <div className="bb-card-preview" role="dialog" aria-modal="true" aria-labelledby="bb-preview-title">
      <button className="bb-preview-scrim" aria-label="Close card preview" onClick={onClose} />
      <section className="bb-print-card">
        <button className="bb-preview-close" onClick={onClose} aria-label="Close">×</button>
        <header>
          <span>✿</span>
          <div><small>PLANT</small><h2 id="bb-preview-title">{species.name}</h2></div>
        </header>
        <div className="bb-print-card-art">
          <LeaflingSprite speciesId={card.id} owner="you" />
        </div>
        <div className="bb-print-card-copy">
          <div className="bb-print-target light"><span>☀️</span><strong>{species.lightNeed}+</strong><small>LIGHT</small></div>
          <div className="bb-print-target water"><span>💧</span><strong>{species.waterMin === species.waterMax ? species.waterMin : `${species.waterMin}–${species.waterMax}`}</strong><small>WATER</small></div>
          <p>{species.blurb}</p>
          <b>{def.text}</b>
          {onPlay && <button className="bb-preview-play" onClick={onPlay}>Choose a pot <span>→</span></button>}
        </div>
      </section>
    </div>
  );
  return (
    <div className="bb-card-preview" role="dialog" aria-modal="true" aria-labelledby="bb-preview-title">
      <button className="bb-preview-scrim" aria-label="Close card preview" onClick={onClose} />
      <section className={`bb-preview-sheet ${def.type}`}>
        <button className="bb-preview-close" onClick={onClose} aria-label="Close">×</button>
        <div className="bb-preview-visual">
          <EffectVignette cardId={card.id} speciesId={def.type === "plant" ? card.id : "calathea"} />
        </div>
        <div className="bb-preview-copy">
          <div className="bb-preview-kicker"><span>{def.icon}</span>{def.type}{def.status ? " · persistent" : " · instant"}</div>
          <h2 id="bb-preview-title">{def.name}</h2>
          <p>{def.text}</p>
          <div className="bb-preview-rule"><small>VALID TARGETS</small><strong>{targetCopy}</strong></div>
          <div className="bb-preview-chips"><span>No mana cost</span><span>Growth checks immediately</span>{def.status && <span>Stays on plant</span>}</div>
          {onPlay && <button className="bb-preview-play" onClick={onPlay}>Play this card<span>→</span></button>}
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------- setup */

function SetupScreen({ onStart, onDemo }) {
  const navigate = useNavigate();
  const [picked, setPicked] = useState(["calathea", "snake", "zz"]);
  const toggle = (id) =>
    setPicked((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : p.length < 4 ? [...p, id] : p
    );
  const valid = picked.length >= 2;

  return (
    <main className="bb-page">
      <div className="bb-setup">
        <button className="bb-back" onClick={() => navigate("/")}>← Leaflings</button>
        <h1>Bloom <i>&amp;</i> Blight</h1>
        <p className="bb-tagline">
          A garden duel. Grow {PETALS_TO_WIN} plants before your rival — one petal per bloom.
        </p>

        <div className="bb-rules">
          <div><strong>🌱 Your turn</strong>Draw a card. Once each: plant a plant, clear a blight, water a plant. Treatments and blights are unlimited.</div>
          <div><strong>🎲 Between turns</strong>The forecast hits <em>every</em> plant: ☀️ +2 light −2 water · ⛅ +1/−1 · 🌧️ +2 water. The next forecast is always visible — plan around it.</div>
          <div><strong>🌸 Growing</strong>Hit a plant's ☀️ target while its 💧 sits in the sweet spot and it instantly blooms — petal banked, out of blight's reach.</div>
        </div>

        <h2>Draft your species</h2>
        <p className="bb-hint-text">
          Pick 2, 3, or 4 — your 12 plant cards split evenly
          {picked.length >= 2 && picked.length <= 4
            ? ` (${picked.length} × ${COPIES_FOR_SPECIES_COUNT[picked.length]} copies)`
            : " (2×6, 3×4, 4×3)"}
          , plus the standard kit of treatments and blights. Easy plants make great closers.
        </p>

        <div className="bb-species-grid">
          {SPECIES_IDS.map((id) => {
            const sp = SPECIES[id];
            const on = picked.includes(id);
            return (
              <button key={id} className={`bb-species ${on ? "on" : ""}`} onClick={() => toggle(id)}>
                <span className="bb-species-sprite"><LeaflingSprite speciesId={id} /></span>
                <strong>{sp.name}</strong>
                <small>☀️{sp.lightNeed} · 💧{sp.waterMin === sp.waterMax ? sp.waterMin : `${sp.waterMin}–${sp.waterMax}`}</small>
                <small className="leaves">{"🌿".repeat(sp.difficulty)}</small>
                <em>{sp.blurb}</em>
                <span className="pick">{on ? "✓ drafted" : "tap to draft"}</span>
              </button>
            );
          })}
        </div>

        <button className="bb-start" disabled={!valid} onClick={() => onStart(picked)}>
          🎲 Roll the biome &amp; play
        </button>
        <p className="bb-hint-text">Sprout drafts a secret deck of their own.</p>
        <button className="bb-demo-link" onClick={onDemo}>
          …or jump into the mid-match demo garden
        </button>
        <p className="bb-footer-cta">
          Bloom &amp; Blight is the web companion to{" "}
          <button onClick={() => openInNewTab(APP_STORE_URL)}>Leaflings for iOS</button> — where
          the plants are real and they talk back.
        </p>
      </div>
    </main>
  );
}

function BiomeRoll({ finalId, onDone }) {
  const [shownId, setShownId] = useState(BIOME_IDS[0]);
  const [landed, setLanded] = useState(false);

  useEffect(() => {
    let i = 0;
    const spin = setInterval(() => { i += 1; setShownId(BIOME_IDS[i % BIOME_IDS.length]); }, 140);
    const stop = setTimeout(() => { clearInterval(spin); setShownId(finalId); setLanded(true); }, 1800);
    const done = setTimeout(onDone, 3300);
    return () => { clearInterval(spin); clearTimeout(stop); clearTimeout(done); };
  }, [finalId, onDone]);

  const biome = BIOMES[shownId];
  return (
    <main className="bb-page">
      <div className="bb-overlay opaque">
        <div className={`bb-roll-card ${landed ? "landed" : "spinning"}`}>
          <small>ROLLING THE BIOME</small>
          <span className="big">{biome.icon}</span>
          <h2>{biome.name}</h2>
          <p>{biome.blurb}</p>
          <div className="bb-odds">
            ☀️ {biome.odds.sunny}/6 · ⛅ {biome.odds.cloudy}/6 · 🌧️ {biome.odds.rain}/6
          </div>
          {landed && <p className="deal">Dealing opening hands…</p>}
        </div>
      </div>
    </main>
  );
}

/* ------------------------------------------------------------- page */

export default function GamePage() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState("setup"); // setup | roll | play
  const [game, setGame] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  const [rollInfo, setRollInfo] = useState(null); // { biomeId, playerSpecies }

  const [selectedUid, setSelectedUid] = useState(null);
  const [mode, setMode] = useState(null); // 'water' | 'clear'
  const [step, setStep] = useState(null); // per-card multi-step state
  const [modal, setModal] = useState(null); // 'recycle' | 'planter'
  const [showLog, setShowLog] = useState(() => window.innerWidth > 1050);
  const [notice, setNotice] = useState("Pick a card or use your once-per-turn actions.");
  const [weatherShow, setWeatherShow] = useState(null);
  const [previewCard, setPreviewCard] = useState(null);
  const aiSteps = useRef(0);

  const meP = game?.players[HUMAN];
  const myTurn = game && game.phase === "play" && game.active === HUMAN && !weatherShow;
  const selected = meP?.hand.find((c) => c.uid === selectedUid);
  const validPlantUids = useMemo(
    () => new Set(selected ? getTargets(game, HUMAN, selected.id).map((p) => p.uid) : []),
    [game, selected]
  );

  const clearSelection = () => { setSelectedUid(null); setMode(null); setStep(null); setModal(null); };

  /* ---- match setup ---- */
  const startMatch = (playerSpecies) => {
    setRollInfo({ biomeId: rollBiome(), playerSpecies });
    setDemoMode(false);
    setScreen("roll");
  };

  const beginRolledMatch = () => {
    const pool = [...SPECIES_IDS].sort(() => Math.random() - 0.5);
    const aiSpecies = pool.slice(0, 2 + Math.floor(Math.random() * 2));
    setGame(newGame({ playerSpecies: rollInfo.playerSpecies, aiSpecies, biomeId: rollInfo.biomeId }));
    clearSelection();
    aiSteps.current = 0;
    setNotice("Your move! Plant something and keep an eye on the forecast.");
    setScreen("play");
  };

  const loadDemo = () => {
    setGame(createDemoGame());
    setDemoMode(true);
    clearSelection();
    aiSteps.current = 0;
    setNotice("Demo garden loaded at turn four — every card type is in your hand.");
    setScreen("play");
  };

  const backToSetup = () => { setGame(null); clearSelection(); setScreen("setup"); };

  /* ---- weather pop-up follows the engine ---- */
  useEffect(() => {
    if (!game?.lastWeather) return undefined;
    setWeatherShow(game.lastWeather.id);
    const t = setTimeout(() => setWeatherShow(null), 1800);
    return () => clearTimeout(t);
  }, [game?.lastWeather?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- Sprout plays one visible action at a time ---- */
  useEffect(() => {
    if (screen !== "play" || !game || game.phase !== "play" || game.active !== AI || weatherShow)
      return undefined;
    const timer = setTimeout(() => {
      aiSteps.current += 1;
      const action = aiSteps.current > AI_STEP_CAP ? { kind: "end" } : chooseAiAction(game);
      let next = game;
      if (action.kind === "plant") next = plantCard(game, AI, action.handUid, action.slot);
      if (action.kind === "compost") next = compostCard(game, AI, action.handUid, action.plantUid);
      if (action.kind === "water") next = waterPlant(game, AI, action.plantUid);
      if (action.kind === "digUp") next = digUpPlant(game, AI, action.plantUid);
      if (action.kind === "treatment") next = playTreatment(game, AI, action.handUid, action.payload);
      if (action.kind === "blight") next = playBlight(game, AI, action.handUid, action.payload);
      if (action.kind === "end" || next === game) {
        // Either Sprout is done, or the heuristic proposed an illegal move —
        // end the turn rather than stalling the match.
        next = endTurn(game);
        aiSteps.current = 0;
      }
      setGame(next);
    }, AI_STEP_MS);
    return () => clearTimeout(timer);
  }, [game, screen, weatherShow]);

  /* ---- hand interaction ---- */

  const canPlay = (card) => {
    if (!game) return false;
    const def = CARDS[card.id];
    const mine = meP.garden.filter(Boolean);
    if (def.type === "plant")
      return (
        (!meP.usedPlantAction && meP.garden.some((s) => s === null)) ||
        getTargets(game, HUMAN, card.id).length > 0
      );
    if (def.type === "blight") return getTargets(game, HUMAN, card.id).length > 0;
    switch (card.id) {
      case "shopping": return true;
      case "recycle": return meP.discard.some((c) => CARDS[c.id].type !== "blight");
      case "planter":
        return !meP.usedPlantAction && meP.garden.some((s) => s === null) &&
          meP.deck.some((c) => CARDS[c.id].type === "plant");
      case "irrigation":
      case "greenhouse": return mine.length > 0;
      default: return getTargets(game, HUMAN, card.id).length > 0;
    }
  };

  const playCard = (card) => {
    if (!myTurn) return;
    setMode(null);
    setStep(null);
    setModal(null);
    if (selectedUid === card.uid) { setSelectedUid(null); return; }
    if (!canPlay(card)) {
      setNotice(`${CARDS[card.id].name} has no valid play right now.`);
      setSelectedUid(null);
      return;
    }
    const def = CARDS[card.id];
    if (card.id === "shopping") {
      setSelectedUid(card.uid);
      setStep({ kind: "shopping" });
      setNotice("Shopping Trip: confirm to draw 2 cards.");
      return;
    }
    setSelectedUid(card.uid);
    if (card.id === "recycle") { setModal("recycle"); return; }
    if (card.id === "planter") { setModal("planter"); return; }
    if (card.id === "fertilizer") {
      setStep({ kind: "fertilizer", water: 1 });
      setNotice("Fertilizer: choose whether to include the +1 💧, then tap your plant.");
    } else if (card.id === "rootRot") {
      setNotice("Root Rot: tap an enemy plant to add 5 💧.");
    } else if (card.id === "irrigation") {
      setStep({ kind: "irrigation" });
      setNotice("Irrigation: tap your plants to split up to 3 💧, then press Done.");
    } else if (card.id === "greenhouse") {
      setStep({ kind: "greenhouse", ops: [] });
      setNotice("Greenhouse: choose an adjustment over up to 3 plants, then confirm.");
    } else if (card.id === "repot") {
      setNotice("Repot: tap a plant with 3+ ☀️ — you'll choose its bonus next.");
    } else if (def.type === "plant") {
      setNotice("Choose an empty pot, or a matching plant to compost it for +1 ☀️ +1 💧.");
    } else {
      setNotice("Glowing plants are valid targets.");
    }
  };

  const selectCard = (card) => {
    if (!myTurn) { setPreviewCard(card); return; }
    if (selectedUid === card.uid) { clearSelection(); return; }
    setPreviewCard(card);
  };

  /* ---- board interaction ---- */

  const resolve = (next, message) => {
    if (next === game) return;
    setGame(next);
    clearSelection();
    if (message) setNotice(message);
  };

  const actOnPlant = (plant, owner, slot) => {
    if (!myTurn) return;

    if (mode === "water" && owner === HUMAN && plant)
      return resolve(waterPlant(game, HUMAN, plant.uid), "Watered. 💧 is spent for this turn.");
    if (mode === "clear" && owner === HUMAN && plant)
      return resolve(digUpPlant(game, HUMAN, plant.uid), "Dug up — the card is shuffled back into your deck.");
    if (!selected) return;

    const def = CARDS[selected.id];

    if (def.type === "plant") {
      if (!plant && owner === HUMAN)
        return resolve(plantCard(game, HUMAN, selected.uid, slot), "Planted! It'll start soaking up the weather.");
      if (plant && owner === HUMAN && plant.speciesId === selected.id)
        return resolve(compostCard(game, HUMAN, selected.uid, plant.uid), "Composted for +1 ☀️ +1 💧.");
      return;
    }

    if (def.type === "blight" && plant && owner === AI) {
      const payload = { plantUid: plant.uid };
      return resolve(playBlight(game, HUMAN, selected.uid, payload), "Blight delivered. 😈");
    }

    if (def.type === "treatment" && plant && owner === HUMAN) {
      if (step?.kind === "irrigation") {
        return resolve(
          playTreatment(game, HUMAN, selected.uid, { plantUid: plant.uid, amount: 3 }),
          "Irrigation ran — +3 💧 delivered."
        );
      }
      if (selected.id === "repot") {
        setStep({ kind: "repotBonus", plantUid: plant.uid });
        setNotice("Repot: pick the finishing touch — +1 ☀️ or +1 💧.");
        return;
      }
      if (selected.id === "fertilizer")
        return resolve(
          playTreatment(game, HUMAN, selected.uid, { plantUid: plant.uid, water: step?.water ?? 1 }),
          "Fertilized."
        );
      return resolve(playTreatment(game, HUMAN, selected.uid, { plantUid: plant.uid }), "Treatment applied.");
    }
  };

  const isTarget = (plant, owner) => {
    if (!myTurn) return false;
    if (mode === "water") return owner === HUMAN && Boolean(plant);
    if (mode === "clear") return owner === HUMAN && Boolean(plant);
    if (!selected || step?.kind === "repotBonus" || modal) return false;
    const def = CARDS[selected.id];
    if (def.type === "plant")
      return owner === HUMAN &&
        ((!plant && !meP.usedPlantAction) || plant?.speciesId === selected.id);
    if (step?.kind === "irrigation")
      return owner === HUMAN && Boolean(plant);
    if (step?.kind === "greenhouse") return false; // per-plant controls handle Greenhouse
    return Boolean(plant && validPlantUids.has(plant.uid));
  };

  const commitPartial = () => {
    if (step?.kind === "greenhouse" && step.ops.length > 0) {
      resolve(playTreatment(game, HUMAN, selectedUid, { ops: step.ops }), "Greenhouse adjusted.");
    }
  };

  const queueGreenhouse = (plantUid, stat, delta) => {
    if (!myTurn || step?.kind !== "greenhouse") return;
    const existing = step.ops.findIndex((op) => op.plantUid === plantUid);
    const op = { plantUid, stat, delta };
    const ops = existing === -1
      ? step.ops.length < 3 ? [...step.ops, op] : step.ops
      : step.ops.map((queued, index) => index === existing ? op : queued);
    setStep({ ...step, ops });
    setNotice(`Greenhouse: ${ops.length} of 3 plants queued. Confirm when ready.`);
  };

  const finishTurn = () => {
    if (!myTurn) return;
    clearSelection();
    aiSteps.current = 0;
    setNotice(`${WEATHER[game.nextForecast].name} is resolving, then Sprout will play.`);
    setGame(endTurn(game));
  };

  /* ---- step controls rendered under the notice ---- */
  const stepControls = () => {
    if (!myTurn) return null;
    if (step?.kind === "fertilizer")
      return (
        <div className="bb-chiprow">
          <button className={`bb-chip ${step.water === 1 ? "active" : ""}`} onClick={() => setStep({ ...step, water: 1 })}>+1 ☀️ and +1 💧</button>
          <button className={`bb-chip ${step.water === 0 ? "active" : ""}`} onClick={() => setStep({ ...step, water: 0 })}>+1 ☀️ only</button>
          <button className="bb-chip cancel" onClick={clearSelection}>cancel</button>
        </div>
      );
    if (step?.kind === "shopping")
      return (
        <div className="bb-chiprow">
          <button className="bb-chip done" onClick={() => resolve(playTreatment(game, HUMAN, selectedUid), "Shopping Trip played — two cards drawn.")}>Confirm: draw 2 ✓</button>
          <button className="bb-chip cancel" onClick={clearSelection}>cancel</button>
        </div>
      );
    if (step?.kind === "irrigation")
      return (
        <div className="bb-chiprow">
          <span className="bb-chip-label">Tap one plant: +3 💧</span>
          <button className="bb-chip cancel" onClick={clearSelection}>cancel</button>
        </div>
      );
    if (step?.kind === "greenhouse")
      return (
        <div className="bb-chiprow">
          <button className="bb-chip done" disabled={step.ops.length === 0} onClick={commitPartial}>Confirm Greenhouse ✓ ({step.ops.length})</button>
          <button className="bb-chip cancel" onClick={clearSelection}>cancel</button>
        </div>
      );
    if (step?.kind === "repotBonus")
      return (
        <div className="bb-chiprow">
          <span className="bb-chip-label">Repot bonus:</span>
          <button className="bb-chip done" onClick={() => resolve(playTreatment(game, HUMAN, selectedUid, { plantUid: step.plantUid, bonus: "light" }), "Repotted: +1 ☀️.")}>+1 ☀️</button>
          <button className="bb-chip done" onClick={() => resolve(playTreatment(game, HUMAN, selectedUid, { plantUid: step.plantUid, bonus: "water" }), "Repotted: +1 💧.")}>+1 💧</button>
          <button className="bb-chip done" onClick={() => resolve(playTreatment(game, HUMAN, selectedUid, { plantUid: step.plantUid, bonus: "drain" }), "Repotted into fresh dry soil: −3 💧.")}>−3 💧</button>
          <button className="bb-chip cancel" onClick={clearSelection}>cancel</button>
        </div>
      );
    if (selected || mode)
      return (
        <div className="bb-chiprow">
          <button className="bb-chip cancel" onClick={clearSelection}>cancel</button>
        </div>
      );
    return null;
  };

  /* ================================================================ render */

  if (screen === "setup") return <SetupScreen onStart={startMatch} onDemo={loadDemo} />;
  if (screen === "roll") return <BiomeRoll finalId={rollInfo.biomeId} onDone={beginRolledMatch} />;

  const weather = WEATHER[game.nextForecast];

  return (
    <main className="bb-page">
      <header className="bb-header">
        <div>
          <button className="bb-back" onClick={() => navigate("/")}>← Leaflings</button>
          <h1>Bloom <i>&amp;</i> Blight</h1>
          <p>Grow four plants before your rival.</p>
        </div>
        <div className="bb-header-actions">
          <button onClick={backToSetup}>✦ New match</button>
          {demoMode && <button onClick={loadDemo}>↻ Reset demo</button>}
          <button onClick={() => setShowLog(!showLog)}>☷ Game log</button>
        </div>
      </header>

      <div className={`bb-layout ${showLog ? "with-log" : ""}`}>
        <section className="bb-board">
          <div className="bb-weatherbar">
            <div>
              <small>BIOME</small>
              <strong title={BIOMES[game.biomeId].blurb}>
                {BIOMES[game.biomeId].icon} {BIOMES[game.biomeId].name}
              </strong>
            </div>
            <div className="forecast">
              <small>NEXT FORECAST</small>
              <strong>{weather.icon} {weather.name}</strong>
              <span>{weather.text}</span>
            </div>
            <div className={`turn-pill ${myTurn ? "yours" : "theirs"}`}>
              {game.phase !== "play" ? "Match over" : myTurn ? `Turn ${game.turn} — your turn` : "Sprout is thinking…"}
            </div>
          </div>

          {[AI, HUMAN].map((owner) => {
            const player = game.players[owner];
            return (
              <section className={`bb-garden ${owner === HUMAN ? "mine" : "opponent"}`} key={owner}>
                <div className="bb-playerline">
                  <div className="bb-avatar">{owner === HUMAN ? "🧑‍🌾" : "🧚"}</div>
                  <div>
                    <strong>{player.name}</strong>
                    <small>
                      {owner === game.active && game.phase === "play"
                        ? "Holding the trowel"
                        : `${player.hand.length} cards in hand · ${player.deck.length} in deck`}
                    </small>
                  </div>
                  <Petals count={player.petals} label={`${player.name}'s score`} />
                </div>
                <div className="bb-plants">
                  {player.garden.map((plant, slot) => (
                    <Plant
                      key={plant?.uid || `empty-${owner}-${slot}`}
                      plant={plant}
                      owner={player.name}
                      enemy={owner === AI}
                  isTarget={isTarget(plant, owner)}
                  onClick={() => actOnPlant(plant, owner, slot)}
                  greenhouseOp={step?.kind === "greenhouse" && owner === HUMAN && plant
                    ? step.ops.find((op) => op.plantUid === plant.uid)
                    : null}
                  greenhouseFull={step?.kind === "greenhouse" && step.ops.length >= 3}
                  onGreenhouseChange={step?.kind === "greenhouse" && owner === HUMAN && plant
                    ? (stat, delta) => queueGreenhouse(plant.uid, stat, delta)
                    : null}
                      badge={
                        mode === "water" && owner === HUMAN && plant ? "Tap to water"
                        : mode === "clear" && owner === HUMAN && plant ? "Tap to dig up"
                        : step?.kind === "irrigation" && plant && step.allocations[plant.uid]
                        ? `+${step.allocations[plant.uid]} 💧 queued`
                        : null
                      }
                    />
                  ))}
                </div>
              </section>
            );
          })}

          <section className="bb-controls">
            <div className="bb-turn-actions">
              <button
                disabled={!myTurn || meP.usedWater || !meP.garden.some(Boolean)}
                className={mode === "water" ? "active" : ""}
                onClick={() => { setMode(mode === "water" ? null : "water"); setSelectedUid(null); setStep(null); }}
              >
                💧 Water <small>{meP.usedWater ? "used" : "1×"}</small>
              </button>
              <button
                disabled={!myTurn || meP.usedPlantAction || !meP.garden.some(Boolean)}
                className={mode === "clear" ? "active" : ""}
                onClick={() => { setMode(mode === "clear" ? null : "clear"); setSelectedUid(null); setStep(null); }}
                title="Shares the plant action: dig up one of your plants, back into your deck"
              >
                🪏 Dig up <small>{meP.usedPlantAction ? "used" : "1×"}</small>
              </button>
              <span className="bb-action-note" title="Planting (by hand or Planter) is once per turn">
                🌱 Plant <small>{meP.usedPlantAction ? "used" : "1×"}</small>
              </span>
              <button className="end" disabled={!myTurn} onClick={finishTurn}>
                End turn <span>→</span>
              </button>
            </div>
            <p className="bb-notice">{notice}</p>
            {stepControls()}
            <div className="bb-hand" aria-label="Your hand">
              {meP.hand.length === 0 && <p className="bb-notice">Hand empty — end the turn to draw.</p>}
              {meP.hand.map((card) => (
                <HandCard
                  key={card.uid}
                  card={card}
                  selected={card.uid === selectedUid}
                  unplayable={myTurn && !canPlay(card)}
                  onClick={() => selectCard(card)}
                />
              ))}
            </div>
            <div className="bb-piles">
              <span>Draw pile <b>{meP.deck.length}</b></span>
              <span>Discard <b>{meP.discard.length}</b></span>
            </div>
          </section>
        </section>

        {showLog && (
          <aside className="bb-log">
            <div className="bb-log-title">
              <div>
                <small>{BIOMES[game.biomeId].name.toUpperCase()} · TURN {game.turn}</small>
                <h2>Garden log</h2>
              </div>
              <button onClick={() => setShowLog(false)}>×</button>
            </div>
            <div className="bb-log-list">
              {[...game.log].reverse().map((entry) => (
                <article key={entry.id} className={`kind-${entry.kind}`}>
                  <span>{entry.icon}</span>
                  <div><small>TURN {entry.turn}</small><p>{entry.text}</p></div>
                </article>
              ))}
            </div>
          </aside>
        )}
      </div>

      {/* card picker modals */}
      {modal === "recycle" && (
        <div className="bb-overlay" onClick={clearSelection}>
          <div className="bb-modal" onClick={(e) => e.stopPropagation()}>
            <h2>♻️ Recycle</h2>
            <p>Take a treatment or plant from your discard pile back into your hand.</p>
            <div className="bb-modal-cards">
              {meP.discard.filter((c) => CARDS[c.id].type !== "blight").map((c) => (
                <HandCard key={c.uid} card={c} onClick={() =>
                  resolve(playTreatment(game, HUMAN, selectedUid, { discardUid: c.uid }), `${CARDS[c.id].name} recycled into your hand.`)
                } />
              ))}
            </div>
            <button className="bb-chip cancel" onClick={clearSelection}>never mind</button>
          </div>
        </div>
      )}
      {modal === "planter" && (
        <div className="bb-overlay" onClick={clearSelection}>
          <div className="bb-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🏺 Planter</h2>
            <p>Plant any plant card straight from your deck (uses your plant action).</p>
            <div className="bb-modal-cards">
              {meP.deck.filter((c) => CARDS[c.id].type === "plant").map((c) => (
                <HandCard key={c.uid} card={c} onClick={() => {
                  const slotIdx = meP.garden.findIndex((s) => s === null);
                  resolve(playTreatment(game, HUMAN, selectedUid, { deckUid: c.uid, slotIdx }), `${CARDS[c.id].name} planted straight from the deck.`);
                }} />
              ))}
            </div>
            <button className="bb-chip cancel" onClick={clearSelection}>never mind</button>
          </div>
        </div>
      )}

      {/* forecast resolution pop-up */}
      {weatherShow && (
        <div className={`bb-overlay bb-weather-cinematic weather-${weatherShow}`}>
          <div className="bb-weather-stage">
            <EffectVignette cardId="weather" speciesId="pilea" />
            <div className="bb-weather-copy"><small>FORECAST RESOLVING</small><h2>{WEATHER[weatherShow].icon} {WEATHER[weatherShow].name}</h2><p>{WEATHER[weatherShow].text}</p></div>
          </div>
        </div>
      )}

      {previewCard && (
        <CardPreview
          card={previewCard}
          onClose={() => setPreviewCard(null)}
          onPlay={myTurn ? () => { const card = previewCard; setPreviewCard(null); playCard(card); } : null}
        />
      )}

      {/* victory */}
      {game.phase === "over" && !weatherShow && (
        <div className="bb-win">
          <div>
            <PetalFlower petals={PETALS_TO_WIN} size={110} />
            <h2>{game.winner === HUMAN ? "Full bloom — you win!" : "Sprout wins!"}</h2>
            <p>
              {game.winner === HUMAN
                ? `Four plants grown under the ${BIOMES[game.biomeId].name} sky. Green thumb confirmed.`
                : "Your rival out-gardened you this time. Shuffle up and take revenge."}
            </p>
            <div className="bb-win-actions">
              <button onClick={backToSetup}>🌱 New match</button>
              {demoMode && <button className="ghost" onClick={loadDemo}>↻ Replay demo</button>}
              <button className="ghost" onClick={() => openInNewTab(APP_STORE_URL)}>
                Grow real plants — Leaflings for iOS
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
